<template>
  <v-dialog v-model="show" fullscreen transition="slide-y-transition" overlay-color="#111" overlay-opacity="0.95"
    :style="`${isAndroidNative ? 'margin-top: 100px; height: 100vh;' : ''} `">
    <v-card class="bg-dark pa-4 connectivity-dialog">
      <v-btn variant="tonal" class="mt-6" @click="close"
        style="position: absolute; top: 12px; right: 12px; z-index: 10; transition: 0.3s ease;color: #fff;">
        {{ $t('closeButton') }}
      </v-btn>

      <div class="content-scroll">
        <h2 class="text-white mb-6 text-center font-weight-bold">{{ $t('connectivity.title') }}</h2>

        <div class="d-flex flex-column align-center text-center" style="gap: 8px">
          <v-icon size="36" color="white">mdi-account</v-icon>
          <span class="text-white font-weight-bold">{{ $t('connectivity.user') }}</span>

          <!-- Custom arrow SVG -->
          <svg class="custom-arrow" width="24" height="24" viewBox="0 0 24 24">
            <path d="M12 5v14M5 12l7 7 7-7" stroke="cyan" stroke-width="2" fill="none" stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>

          <div class="text-caption text-grey-lighten-1 mt-1">{{ $t('connectivity.device') }}</div>
          <v-card class="connection-card">
            <v-icon size="28" color="cyan">mdi-cellphone-link</v-icon>
            <span class="mt-1 text-white">{{ $t('connectivity.app') }}</span>
          </v-card>

          <!-- Custom arrow SVG -->
          <svg class="custom-arrow" width="24" height="24" viewBox="0 0 24 24">
            <path d="M12 5v14M5 12l7 7 7-7" stroke="cyan" stroke-width="2" fill="none" stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>

          <div class="text-caption text-grey-lighten-1 mt-1">Pinestack SaaS</div>
          <v-card class="connection-card">
            <v-icon size="28" color="cyan">mdi-server</v-icon>
            <span class="mt-1 text-white">{{ $t('connectivity.platform') }}</span>
            <small class="text-grey-lighten-1">{{ $t('connectivity.platformSubtitle') }}</small>
          </v-card>

          <!-- Custom arrow SVG -->
          <svg class="custom-arrow" width="24" height="24" viewBox="0 0 24 24">
            <path d="M12 5v14M5 12l7 7 7-7" stroke="cyan" stroke-width="2" fill="none" stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>
        </div>

        <v-card class="system-wrapper mt-6">
          <h3 class="text-white text-center font-weight-bold">{{ $t('connectivity.customerSystems') }}</h3>
          <v-row class="mt-4" justify="center" no-gutters>
            <!-- Microsoft -->
            <v-col cols="12" md="4" class="pa-2">
              <v-card class="system-card">
                <div class="text-caption font-weight-bold mb-2 text-white">{{ $t('connectivity.microsoft') }}</div>
                <div class="chip-row">
                  <v-chip v-for="(item, i) in $tm('connectivity.microsoftItems')" :key="'ms-' + i" color="blue"
                    text-color="white" class="ma-1" label>
                    {{ item }}
                  </v-chip>
                </div>
              </v-card>
            </v-col>

            <!-- IoT -->
            <v-col cols="12" md="4" class="pa-2">
              <v-card class="system-card">
                <div class="text-caption font-weight-bold mb-2 text-white">{{ $t('connectivity.iot') }}</div>
                <div class="chip-row">
                  <v-chip v-for="(item, i) in $tm('connectivity.iotItems')" :key="'iot-' + i" color="pink"
                    text-color="white" class="ma-1" label>
                    {{ item }}
                  </v-chip>
                </div>
              </v-card>
            </v-col>

            <!-- 3rd Party -->
            <v-col cols="12" md="4" class="pa-2">
              <v-card class="system-card">
                <div class="text-caption font-weight-bold mb-2 text-white">{{ $t('connectivity.thirdParty') }}</div>
                <div class="chip-row">
                  <v-chip v-for="(item, i) in $tm('connectivity.thirdPartyItems')" :key="'tp-' + i" color="amber"
                    text-color="black" class="ma-1" label>
                    {{ item }}
                  </v-chip>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-card>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, defineExpose } from 'vue';

import { Capacitor } from '@capacitor/core';
const isAndroidNative = Capacitor.getPlatform() === 'android' && Capacitor.isNativePlatform();
const show = ref(false);
function openDialog() {
  show.value = true;
}
function close() {
  show.value = false;
}
defineExpose({ openDialog });
</script>

<style scoped>
.bg-dark {
  background-color: #111;
  min-height: 100vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.connectivity-dialog {
  width: 100%;
  max-width: 1200px;
  margin: auto;
}

.content-scroll {
  width: 100%;
  max-width: 1200px;
  padding: 16px;
  padding-top: 64px;
  box-sizing: border-box;
}

.connection-card {
  background: #222;
  color: white;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 0 20px rgba(0, 255, 255, 0.15);
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 220px;
  text-align: center;
  margin-bottom: 12px;
}

.system-wrapper {
  background: #1a1a1a;
  border-radius: 20px;
  padding: 24px;
  width: 100%;
  max-width: 1100px;
  box-shadow: 0 0 30px rgba(0, 255, 255, 0.07);
}

.system-card {
  background-color: #2a2a2a;
  color: white;
  padding: 16px;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 255, 255, 0.05);
  min-height: 130px;
  text-align: center;
}

.chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
}

.custom-arrow {
  margin-top: 8px;
  animation: glowPulse 2s infinite ease-in-out;
  filter: drop-shadow(0 0 6px rgba(0, 255, 255, 0.5));
}

@keyframes glowPulse {

  0%,
  100% {
    filter: drop-shadow(0 0 6px rgba(0, 255, 255, 0.4));
  }

  50% {
    filter: drop-shadow(0 0 12px rgba(0, 255, 255, 0.8));
  }
}

/* Scrollbar styling */
::-webkit-scrollbar {
  width: 4px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: rgba(0, 255, 255, 0.15);
  border-radius: 8px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 255, 255, 0.3);
}

* {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 255, 255, 0.15) transparent;
}
</style>
